import React, { useCallback, useEffect, useMemo, useRef } from 'react';
import styled, { css } from 'styled-components';
import { Typography } from '@mui/material';
import { useNavigate } from 'react-router-dom';
import { useRecoilState, useSetRecoilState } from 'recoil';
import { ReactComponent as MergeIcon } from '../../../../assets/20x20/merge.svg';
import useSelectedEnv from '../../../../hooks/useSelectedEnv';
import useGraphqlQuery from '../../../../hooks/useGraphqlQuery';
import { MERGE_INCIDENT_IDS_PARAM } from '../../../../constants/urlParams';
import { AGENT_MERGE_INCIDENT_TITLES } from '../../../../constants/queryKeys';
import { agentMergeIncidentsPlan, aiChatPanel } from '../../../../atoms/atoms';
import type { ToolRendererArgs } from '../../types';
import type { ApprovalToolRendererProps } from '../../components/ApprovalToolRenderer';
import { Flex, FlexColumn } from '../../../Flex';
import Button from '../../../Button';
import SvgIcon from '../../../SvgIcon';
import { getIncidentTitlesQuery } from './api';
import type { IncidentTitlesResponse } from './types';
import { buildNewArgsFromPlan, planFromToolArgs } from './utils';

const MERGE_PATH = '/incidents/merge';

const PlanCard = styled(FlexColumn)<{ $status?: 'confirmed' | 'rejected' }>`
  border: 1px solid ${({ theme }) => theme.palette.common.gray17};
  border-radius: 8px;
  overflow: hidden;

  ${({ $status, theme }) =>
    $status === 'confirmed' &&
    css`
      border-color: ${theme.palette.common.green100};
    `}

  ${({ $status, theme }) =>
    $status === 'rejected' &&
    css`
      border-color: ${theme.palette.common.red100};
    `}
`;

const PlanHeader = styled(Flex)<{ $status?: 'confirmed' | 'rejected' }>`
  align-items: center;
  gap: 8px;
  padding: 10px 14px;
  background: ${({ theme }) => theme.palette.common.gray9};

  ${({ $status, theme }) =>
    $status === 'confirmed' &&
    css`
      background: ${theme.palette.common.green10};
    `}

  ${({ $status, theme }) =>
    $status === 'rejected' &&
    css`
      background: ${theme.palette.common.red10};
    `}
`;

const PlanBody = styled(FlexColumn)`
  padding: 12px 14px;
  gap: 16px;
`;

const ViewLink = styled(Flex)`
  align-items: center;
  gap: 4px;
  font-size: 13px;
  color: ${({ theme }) => theme.palette.common.blue4};
  cursor: pointer;

  &:hover {
    text-decoration: underline;
  }
`;

const SectionLabel = styled(Typography)`
  font-size: 12px;
  font-weight: 600;
  color: ${({ theme }) => theme.palette.common.gray14};
`;

const IncidentList = styled.ul`
  margin: 4px 0 0;
  padding-left: 18px;

  li {
    font-size: 13px;
    color: ${({ theme }) => theme.palette.common.gray4};
    word-break: break-word;
  }
`;

const IncidentValue = styled(Typography)`
  font-size: 13px;
  color: ${({ theme }) => theme.palette.common.gray4};
  word-break: break-word;
  margin-top: 4px;
`;

type InnerProps = {
  toolArgs: ToolRendererArgs;
  toolCallId: string;
  resultIsError?: boolean;
  onApproveToolUse: (
    approved: boolean,
    newArgs?: Record<string, unknown>,
  ) => void;
  isActive?: boolean;
};

const MergeIncidentsToolRendererInner = ({
  toolArgs,
  toolCallId,
  resultIsError,
  onApproveToolUse,
  isActive,
}: InnerProps) => {
  const envId = useSelectedEnv();
  const navigate = useNavigate();
  const [plan, setPlan] = useRecoilState(agentMergeIncidentsPlan);
  const setChatPanel = useSetRecoilState(aiChatPanel);
  const responseRef = useRef<'confirmed' | 'rejected' | null>(null);

  useEffect(() => {
    if (!isActive) {
      return;
    }
    setPlan((prev) => {
      if (prev?.toolCallId === toolCallId) {
        return prev;
      }
      return planFromToolArgs(toolArgs, toolCallId);
    });
  }, [isActive, setPlan, toolArgs, toolCallId]);

  useEffect(() => {
    if (
      !isActive ||
      !plan?.userResponse ||
      plan.agentAcknowledged ||
      plan.toolCallId !== toolCallId
    ) {
      return;
    }
    responseRef.current = plan.userResponse;
    if (plan.userResponse === 'confirmed') {
      onApproveToolUse(true, buildNewArgsFromPlan(plan));
    } else {
      onApproveToolUse(false);
    }
    setPlan((prev) => (prev ? { ...prev, agentAcknowledged: true } : prev));
  }, [isActive, onApproveToolUse, plan, setPlan, toolCallId]);

  useEffect(() => {
    if (isActive || !plan || plan.toolCallId !== toolCallId) {
      return;
    }
    setPlan(null);
  }, [isActive, plan, toolCallId, setPlan]);

  const snapshotFromToolArgs = useCallback(() => {
    setPlan((prev) => {
      const base = planFromToolArgs(toolArgs, toolCallId);
      if (prev?.toolCallId === toolCallId) {
        return { ...base, ...prev };
      }
      return base;
    });
  }, [setPlan, toolArgs, toolCallId]);

  const handleReviewClick = useCallback(
    (e: React.MouseEvent) => {
      e.preventDefault();
      snapshotFromToolArgs();
      setChatPanel((prev) => ({ ...prev, isFullWidth: false }));
      const primary = String(toolArgs?.primary_incident_id ?? '');
      const sources = (toolArgs?.incident_ids as string[] | undefined) ?? [];
      const allIds = [primary, ...sources.filter((id) => id !== primary)];
      const params = new URLSearchParams();
      params.set(MERGE_INCIDENT_IDS_PARAM, allIds.join(','));
      navigate(`/${envId}${MERGE_PATH}?${params.toString()}`);
    },
    [envId, navigate, setChatPanel, snapshotFromToolArgs, toolArgs],
  );

  const handleApprove = useCallback(() => {
    setPlan((prev) => {
      const base =
        prev?.toolCallId === toolCallId
          ? prev
          : planFromToolArgs(toolArgs, toolCallId);
      return { ...base, userResponse: 'confirmed' as const };
    });
  }, [setPlan, toolArgs, toolCallId]);

  const handleReject = useCallback(() => {
    setPlan((prev) => {
      const base =
        prev?.toolCallId === toolCallId
          ? prev
          : planFromToolArgs(toolArgs, toolCallId);
      return { ...base, userResponse: 'rejected' as const };
    });
  }, [setPlan, toolArgs, toolCallId]);

  const primary = String(toolArgs?.primary_incident_id ?? '');
  const sources = useMemo(
    () => (toolArgs?.incident_ids as string[] | undefined) ?? [],
    [toolArgs],
  );
  const n = sources.length + 1;

  const allIncidentIds = useMemo(() => {
    const set = new Set<string>();
    if (primary) set.add(primary);
    sources.forEach((id) => set.add(id));
    return Array.from(set);
  }, [primary, sources]);

  const { data: titlesResponse } = useGraphqlQuery<IncidentTitlesResponse>({
    queryKey: [AGENT_MERGE_INCIDENT_TITLES, allIncidentIds],
    query: getIncidentTitlesQuery,
    variables: {
      filters: { incidentIds: allIncidentIds },
      limit: allIncidentIds.length,
    },
    enabled: allIncidentIds.length > 0,
  });

  const titlesById = useMemo(() => {
    const map = new Map<string, string>();
    titlesResponse?.incidentsV2Paged?.items?.forEach((item) => {
      map.set(item.id, item.title);
    });
    return map;
  }, [titlesResponse]);

  const labelFor = useCallback(
    (id: string) => titlesById.get(id) ?? id,
    [titlesById],
  );

  const completedFromResult: 'confirmed' | 'rejected' | undefined =
    !isActive && resultIsError !== undefined
      ? resultIsError
        ? 'rejected'
        : 'confirmed'
      : undefined;

  const status: 'confirmed' | 'rejected' | undefined =
    responseRef.current ??
    (plan?.agentAcknowledged && plan.toolCallId === toolCallId
      ? plan.userResponse
      : undefined) ??
    completedFromResult;

  const headerLabel = useMemo(() => {
    if (status === 'confirmed') {
      return 'Merge approved';
    }
    if (status === 'rejected') {
      return 'Merge rejected';
    }
    return `Merge ${n} incidents`;
  }, [n, status]);

  const showActions =
    isActive &&
    plan?.toolCallId === toolCallId &&
    !plan.userResponse &&
    !plan.agentAcknowledged;

  return (
    <PlanCard $status={status}>
      <PlanHeader $status={status}>
        <SvgIcon width={16} height={16} fillColor="gray14">
          <MergeIcon />
        </SvgIcon>
        <Typography
          sx={{
            fontSize: 13,
            fontWeight: 600,
            color:
              status === 'confirmed'
                ? 'common.green100'
                : status === 'rejected'
                ? 'common.red100'
                : undefined,
          }}
        >
          {headerLabel}
        </Typography>
      </PlanHeader>
      <PlanBody>
        <FlexColumn sx={{ gap: '4px' }}>
          <SectionLabel>Merge incidents:</SectionLabel>
          <IncidentList>
            {sources.map((id) => (
              <li key={id}>{labelFor(id)}</li>
            ))}
          </IncidentList>
        </FlexColumn>
        <FlexColumn sx={{ gap: '4px' }}>
          <SectionLabel>Primary incident:</SectionLabel>
          <IncidentValue>{labelFor(primary)}</IncidentValue>
        </FlexColumn>
        <Flex sx={{ alignItems: 'center', justifyContent: 'space-between' }}>
          <ViewLink onClick={handleReviewClick}>Review and merge</ViewLink>

          {showActions && (
            <Flex sx={{ gap: 1 }}>
              <Button
                variant="outlined"
                size="extraSmall"
                onClick={handleReject}
              >
                Reject
              </Button>
              <Button
                variant="contained"
                size="extraSmall"
                onClick={handleApprove}
              >
                Approve
              </Button>
            </Flex>
          )}
        </Flex>
      </PlanBody>
    </PlanCard>
  );
};

const mergeIncidentsApprovalRenderer = ({
  invocation,
  onApproveToolUse,
  isActive,
}: ApprovalToolRendererProps) => (
  <MergeIncidentsToolRendererInner
    toolArgs={invocation.args ?? {}}
    toolCallId={invocation.toolCallId}
    resultIsError={invocation.result?.isError}
    onApproveToolUse={onApproveToolUse}
    isActive={isActive}
  />
);

export default mergeIncidentsApprovalRenderer;
